"use client"

import { useSyncExternalStore } from "react"
import type { TextDirection } from "@/lib/i18n/direction"
import { useTextDirection } from "@/lib/i18n/use-text-direction"

/** <html dir>, or null where the document names no direction. */
function readDocumentDirection(): TextDirection | null {
  if (typeof document === "undefined") return null
  const dir = document.documentElement.getAttribute("dir")
  return dir === "rtl" || dir === "ltr" ? dir : null
}

function subscribeToDocumentDirection(onChange: () => void): () => void {
  if (typeof MutationObserver === "undefined") return () => {}
  const observer = new MutationObserver(onChange)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["dir"] })
  return () => observer.disconnect()
}

/** The server cannot see the <html dir> the inline locale script will set. */
const unknownOnTheServer = (): TextDirection | null => null

/**
 * The direction the page is laid out in right now: <html dir>. Null where
 * that is not known: on the server, in the render that hydrates the server's
 * markup, and where the document names no direction.
 *
 * The inline locale script (lib/i18n/locale-bootstrap.tsx) sets <html dir>
 * before the first paint and the i18n provider keeps it in step, while the
 * React locale reads English until a saved catalogue has loaded (locale audit
 * 2026-09-26, RTL). Anything placed against the stylesheet's layout, such as
 * the Feedback FAB's corner, a table's reserve for it, or a left-to-right
 * island handing its content back, follows the document, or an Arabic page
 * shows it the wrong way round until the catalogue arrives.
 */
export function useDocumentDirection(): TextDirection | null {
  return useSyncExternalStore(subscribeToDocumentDirection, readDocumentDirection, unknownOnTheServer)
}

/** useDocumentDirection, or the interface language's direction where the document does not say. */
export function usePageDirection(): TextDirection {
  const fromLocale = useTextDirection()
  return useDocumentDirection() ?? fromLocale
}
